import React, { useState } from "react";
import { MdClose, MdOutlineClose } from "react-icons/md";
import useTeacherInfo from "../../../hooks/useTeacherInfo";
import DaumPostcodeEmbed from "react-daum-postcode";
import { convertToYearMonthDayFormat } from "../../../util/formatdate";
import { useUIContext } from "../../../context/UIContext";
import { useTranslation } from "react-i18next";

export default function UpdateTeacherInfoModal({
  setModalOpen,
  teacherInfo,
  setTeacherInfo,
  setIsUpLoading,
}) {
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [modifiedFields, setModifiedFields] = useState({});
  const [openPostCode, setOpenPostCode] = useState(false);

  const { handleToastCenterTop } = useUIContext();

  const { updateTeacherInfo, createTeacherInfo } = useTeacherInfo();

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "entrance_date" || name === "termination_date") {
      formattedValue = convertToYearMonthDayFormat(value);
    }
    setTeacherInfo((teacherInfo) => ({
      ...teacherInfo,
      [name]: formattedValue,
    }));

    // 변경된 필드 추적
    setModifiedFields((fields) => ({
      ...fields,
      [name]: formattedValue,
    }));
  };

  const handlePostcode = (data) => {
    let fullAddress = data.address;
    let extraAddress = "";

    if (data.addressType === "R") {
      if (data.bname !== "") {
        extraAddress += data.bname;
      }
      if (data.buildingName !== "") {
        extraAddress +=
          extraAddress !== "" ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== "" ? ` (${extraAddress})` : "";
    }

    setTeacherInfo((teacherInfo) => ({
      ...teacherInfo,
      zipcode: data.zonecode,
      address: fullAddress,
    }));

    // 변경된 필드 추적
    setModifiedFields((fields) => ({
      ...fields,
      zipcode: data.zonecode,
      address: fullAddress,
    }));

    setOpenPostCode(false);
    // console.log(fullAddress);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 변경된 부분이 없는지 확인
    if (Object.keys(modifiedFields).length === 0) {
      handleToastCenterTop(
        t("common.messages.noChange") || "변경된 내용이 없습니다."
      );
      return;
    }

    setIsUpLoading(true);

    let dataToUpdate = {
      id: teacherInfo.id,
      ...modifiedFields,
    };

    try {
      const handleSuccess = (successMessage) => {
        console.log(successMessage);
        setIsSaved(true);
        setTimeout(() => {
          setIsSaved(false);
          setModalOpen(false);
        }, 1000);
      };

      const handleError = (error) => {
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        alert(
          (t("common.messages.errorAlert") || "오류가 발생했습니다: ") +
            error.message
        );
      };

      if (teacherInfo.id) {
        updateTeacherInfo.mutate(dataToUpdate, {
          onSuccess: handleSuccess,
          onError: handleError,
        });
      } else {
        createTeacherInfo.mutate(teacherInfo, {
          onSuccess: handleSuccess,
          onError: handleError,
        });
      }
    } catch (error) {
      console.error(error); // 일단 콘솔에 에러를 출력합니다.
      console.error("위와 같은 에러가 났으니 고치삼");
      alert(
        (t("common.messages.errorAlert") || "오류가 발생했습니다: ") +
          error.message
      );
    } finally {
      setIsUpLoading(false);
    }
  };

  return (
    <>
      <div className="modal-container">
        <div
          className="modalForUnit"
          style={{ width: "80%", maxHeight: "95%", overflowY: "auto" }}
        >
          <div className="modal-header d-flex justify-content-between">
            <h3>
              {t("admin.teacherInfo.teacherInfoModify") || "교직원 정보 수정"}
            </h3>
            <MdClose
              size={27}
              type="button"
              onClick={() => {
                setModalOpen(false);
              }}
            />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              <div className="row mb-2">
                <div className="col-sm-2 col-4">
                  <label htmlFor="fullname" className="col-form-label">
                    {t("common.user.fullname") || "이름"}
                  </label>
                </div>
                <div className="col-sm-3 col-8">
                  <input
                    type="text"
                    name="fullname"
                    className="form-control"
                    value={teacherInfo?.fullname}
                    disabled
                  />
                </div>

                <div className="col-sm-2 col-4">
                  <label htmlFor="username" className="col-form-label">
                    {t("common.user.username") || "아이디"}
                  </label>
                </div>
                <div className="col-sm-4 col-8">
                  <input
                    type="text"
                    name="username"
                    className="form-control"
                    value={teacherInfo?.username}
                    disabled
                  />
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-2 col-4">
                  <label htmlFor="entrance_date" className="col-form-label">
                    {t("admin.teacherInfo.entranceDate") || "입사일"}
                  </label>
                </div>
                <div className="col-sm-4 col-8">
                  <input
                    type="date"
                    name="entrance_date"
                    className="form-control"
                    value={teacherInfo?.entrance_date || ""}
                    onChange={handleModalChange}
                  />
                </div>

                <div className="col-sm-2 col-4">
                  <label htmlFor="entrance_date" className="col-form-label">
                    {t("admin.teacherInfo.terminationDate") || "퇴사일"}
                  </label>
                </div>
                <div className="col-sm-4 col-8">
                  <input
                    type="date"
                    name="termination_date"
                    className="form-control"
                    value={teacherInfo?.termination_date || ""}
                    onChange={handleModalChange}
                  />
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-3 col-6">
                  <input
                    type="text"
                    name="zipcode"
                    className="form-control"
                    placeholder={t("admin.teacherInfo.zipcode") || "우편번호"}
                    value={teacherInfo?.zipcode || ""}
                    onChange={handleModalChange}
                  />
                </div>
                <div className="col-sm-3 col-6">
                  <input
                    type="button"
                    className="form-control bg-primary-subtle"
                    onClick={() => setOpenPostCode(true)}
                    value={
                      t("admin.teacherInfo.zipcodeSearch") || "우편번호 검색"
                    }
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <input
                    type="text"
                    name="address"
                    className="form-control"
                    placeholder={t("admin.teacherInfo.address") || "주소"}
                    value={teacherInfo?.address || ""}
                    onChange={handleModalChange}
                  />
                </div>
                <div className="col-12">
                  <input
                    type="text"
                    name="address_detail"
                    placeholder={
                      t("admin.teacherInfo.addressDetail") || "상세주소"
                    }
                    className="form-control"
                    value={teacherInfo?.address_detail || ""}
                    onChange={handleModalChange}
                  />
                </div>
              </div>

              {openPostCode && (
                <div className="row mb-2">
                  <div className="col-11 pe-0">
                    <DaumPostcodeEmbed
                      onComplete={handlePostcode}
                      style={{ width: "100%", height: 450, marginTop: 10 }}
                      className={"border"}
                      autoClose={false}
                    />
                  </div>
                  <div className="col-1 ps-1">
                    <MdOutlineClose
                      size={25}
                      role="button"
                      onClick={() => setOpenPostCode(false)}
                    />
                  </div>
                </div>
              )}
              <div className="row mb-2">
                <div className="col-sm-2 col-4">
                  <label htmlFor="phone_number" className="col-form-label">
                    {t("admin.teacherInfo.teacherPhone") || "교직원 전화번호"}
                  </label>
                </div>
                <div className="col-sm-3 col-8">
                  <input
                    type="text"
                    name="phone_number"
                    className="form-control"
                    value={teacherInfo?.phone_number || ""}
                    onChange={handleModalChange}
                  />
                </div>

                <div className="col-sm-2 col-4">
                  <label htmlFor="korean_id_no" className="col-form-label">
                    {t("admin.teacherInfo.registerationNo") || "주민등록번호"}
                  </label>
                </div>
                <div className="col-sm-3 col-8">
                  <input
                    type="text"
                    name="korean_id_no"
                    className="form-control"
                    value={teacherInfo?.korean_id_no || ""}
                    onChange={handleModalChange}
                  />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-sm-2 col-4">
                  <label htmlFor="memo" className="col-form-label">
                    {t("admin.teacherInfo.position") || "직위(직급)"}
                  </label>
                </div>
                <div className="col-sm-10 col-8">
                  <input
                    name="position"
                    className="form-control"
                    value={teacherInfo?.position || ""}
                    onChange={handleModalChange}
                  />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-sm-2 col-4">
                  <label htmlFor="memo" className="col-form-label">
                    {t("admin.teacherInfo.memo") || "메모"}
                  </label>
                </div>
                <div className="col-sm-10 col-8">
                  <textarea
                    name="memo"
                    className="form-control"
                    rows="1"
                    value={teacherInfo?.memo || ""}
                    onChange={handleModalChange}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                {t("common.labels.cancel") || "취소"}
              </button>
              {isSaved ? (
                <button type="submit" className="btn btn-primary" disabled>
                  {t("common.labels.savedWell")}
                </button>
              ) : (
                <button type="submit" className="btn btn-primary">
                  {t("common.labels.doSave")}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
